import { Inject, Injectable, NgZone, OnDestroy } from "@angular/core";
import {
    BehaviorSubject,
    debounceTime,
    delay,
    filter,
    map,
    Observable,
    startWith,
    Subscription,
    switchMap,
    take,
    tap,
} from "rxjs";

import { LOCALE, WINDOW } from "@hermes/app-core";
import { Locale } from "@hermes/locale";
import {
    LoadGeoLibrariesService,
    BAIDU_ENGINE_KEY,
    PIN_DARK_MARKER_CLUSTERER,
} from "@hermes/map-utils";
import { ExternalLibraryFacade } from "@hermes/states/external-library";

import {
    addHoverListeners,
    getBMapTextIconOverlays,
    getMarkers,
} from "../helpers/bmap.helper";
import {
    BAIDU_MAP_WINDOW_ATTRIBUTE,
    BAIDU_MARKER_CLUSTERER,
    BAIDU_MARKER_CLUSTERER_ASSETS_URL,
    BAIDU_TEXT_ICON_OVERLAY,
    BAIDU_TEXT_ICON_OVERLAY_ASSETS_URL,
} from "../map-baidu.constant";
import { BMapLibMarkerClusterer } from "../marker-clusterer";

export type MovingEvent = "ondragend" | "onzoomend";

const isDefinedBMap = (baiduMap: BMap.Map | undefined): baiduMap is BMap.Map =>
    baiduMap !== undefined;

const isDefinedBClusterer = (
    baiduMarkerClusterer: BMapLibMarkerClusterer | undefined,
): baiduMarkerClusterer is BMapLibMarkerClusterer =>
    baiduMarkerClusterer !== undefined;

@Injectable({ providedIn: "root" })
export class MapBaiduService implements OnDestroy {
    private subscription: Subscription = new Subscription();

    /**
     * The Baidu map instance MUST remain private.
     *
     * To interact with the Map, you need to use the service.
     */
    private baiduMap$: BehaviorSubject<BMap.Map | undefined> =
        new BehaviorSubject<BMap.Map | undefined>(undefined);

    private baiduMarkerClusterer$: BehaviorSubject<
        BMapLibMarkerClusterer | undefined
    > = new BehaviorSubject<BMapLibMarkerClusterer | undefined>(undefined);

    /**
     * Observable that describes on the Map is initialized.
     */
    public mapInitialized$: Observable<boolean> = this.baiduMap$.pipe(
        map((baiduMap) => !!baiduMap),
    );

    constructor(
        private loadGeoLibrariesService: LoadGeoLibrariesService,
        private ngZone: NgZone,
        public externalLibraryFacade: ExternalLibraryFacade,
        @Inject(LOCALE) private locale: Locale,
        @Inject(WINDOW)
        private window: Window,
    ) {}

    public createMap(element: HTMLElement): void {
        this.subscription.add(
            // If the Baidu lib is loaded ...
            this.loadGeoLibrariesService
                .loadAndGetBaiduStatus()
                .pipe(
                    filter((isLoaded) => isLoaded),
                    take(1),
                    map(() => new BMap.Map(element)),
                    // Delay is needed to get markers on mobile mode too.
                    // Otherwise the map is not initialized at the right moment.
                    delay(1),
                )
                .subscribe((baiduMap) => {
                    // Then we build the Map when the baiduMap is loaded.
                    // We need to run it into NgZone to synchronize with the other zones,
                    // otherwise we have to click to get markers on desktop mode.
                    baiduMap.addEventListener("load", () => {
                        this.ngZone.run(() => {
                            this.baiduMap$.next(baiduMap);
                        });
                    });
                    // With Baidu, when need to call "centerAndZoom" to get the map displayed.
                    const point = new BMap.Point(0, 0);
                    baiduMap.centerAndZoom(point, 11);
                }),
        );
    }

    public loadAndCreateMarkerClusterer(): void {
        const textIconUrl = `${this.locale.urlPrefix}${BAIDU_TEXT_ICON_OVERLAY_ASSETS_URL}`;
        const markerClustererUrl = `${this.locale.urlPrefix}${BAIDU_MARKER_CLUSTERER_ASSETS_URL}`;

        this.subscription.add(
            this.externalLibraryFacade
                .isLoaded$(BAIDU_ENGINE_KEY)
                .pipe(
                    filter((isLoaded) => isLoaded),
                    tap(() => {
                        this.externalLibraryFacade.load({
                            key: BAIDU_TEXT_ICON_OVERLAY,
                            windowAttribute: BAIDU_MAP_WINDOW_ATTRIBUTE,
                            url: textIconUrl,
                        });
                    }),
                    switchMap(() =>
                        this.externalLibraryFacade.isLoaded$(
                            BAIDU_TEXT_ICON_OVERLAY,
                        ),
                    ),
                    filter((isLoaded) => isLoaded),
                    tap(() => {
                        this.externalLibraryFacade.load({
                            key: BAIDU_MARKER_CLUSTERER,
                            windowAttribute: BAIDU_MAP_WINDOW_ATTRIBUTE,
                            url: markerClustererUrl,
                        });
                    }),
                    switchMap(() =>
                        this.externalLibraryFacade.isLoaded$(
                            BAIDU_MARKER_CLUSTERER,
                        ),
                    ),
                    filter((isLoaded) => isLoaded),
                    take(1),
                )
                .subscribe(() => {
                    this.addMarkerClusterer();
                }),
        );
    }

    public getMovingEventListener = (): Observable<MovingEvent> =>
        this.baiduMap$.pipe(
            filter(isDefinedBMap),
            take(1),
            switchMap(
                (baiduMap) =>
                    new Observable<MovingEvent>((subscriber) => {
                        (["onzoomend", "onmoveend"] as MovingEvent[]).forEach(
                            (event) => {
                                baiduMap.addEventListener(event, () => {
                                    subscriber.next(event);
                                });
                            },
                        );
                    }),
            ),
        );

    public getBounds = (): Observable<BMap.Bounds> =>
        this.baiduMap$.pipe(
            filter(isDefinedBMap),
            take(1),
            map((baiduMap) => baiduMap.getBounds()),
        );

    public addMapOptions(): void {
        this.subscription.add(
            this.baiduMap$
                .pipe(filter(isDefinedBMap), take(1))
                .subscribe((baiduMap) => {
                    baiduMap.enableScrollWheelZoom();
                    baiduMap.enableKeyboard();
                    baiduMap.enablePinchToZoom();
                }),
        );
    }

    public addMapControls(options: BMap.NavigationControlOptions): void {
        this.subscription.add(
            this.baiduMap$
                .pipe(filter(isDefinedBMap), take(1))
                .subscribe((baiduMap) => {
                    const navCtrl = new BMap.NavigationControl(options);
                    baiduMap.addControl(navCtrl);
                }),
        );
    }

    public addMarker(marker: BMap.Marker): void {
        this.subscription.add(
            this.baiduMap$
                .pipe(filter(isDefinedBMap), take(1))
                .subscribe((baiduMap) => {
                    baiduMap.addOverlay(marker);
                }),
        );
    }

    public addMarkerClusterer = (): void => {
        this.subscription.add(
            this.baiduMap$
                .pipe(
                    filter(isDefinedBMap),
                    map(
                        (baiduMap) =>
                            new this.window.BMapLib.MarkerClusterer(baiduMap, {
                                markers: getMarkers(baiduMap.getOverlays()),
                            }),
                    ),
                    tap((clusterer) =>
                        clusterer.setStyles([
                            {
                                url: `${this.locale.urlPrefix}${PIN_DARK_MARKER_CLUSTERER}`,
                                size: new BMap.Size(35, 45),
                                textColor: "#ffffff",
                            },
                        ]),
                    ),
                    take(1),
                )
                .subscribe((clusterer) => {
                    this.baiduMarkerClusterer$.next(clusterer);
                }),
        );

        /**
         * With each map movement, the cluster is re-created. It's not really cool but it's Baidu who manages it...
         *
         * So you have to re-create the events listener each time.
         */
        this.subscription.add(
            this.baiduMarkerClusterer$
                .asObservable()
                .pipe(
                    filter(isDefinedBClusterer),
                    switchMap(() => this.getMovingEventListener()),
                    debounceTime(50),
                    startWith("onmoveend"),
                    switchMap(() => this.baiduMap$),
                    filter(isDefinedBMap),
                )
                .subscribe((baiduMap) => {
                    const overlays = getBMapTextIconOverlays(
                        baiduMap.getOverlays(),
                        this.window,
                    );
                    addHoverListeners(overlays, this.locale);
                }),
        );
    };

    public getZoom = (): Observable<number> =>
        this.baiduMap$.pipe(
            filter(isDefinedBMap),
            map((baiduMap) => baiduMap.getZoom()),
            take(1),
        );

    public setCenterAndZoom = (center: BMap.Point, zoom: number): void => {
        this.subscription.add(
            this.baiduMap$
                .pipe(filter(isDefinedBMap), take(1))
                .subscribe((baiduMap) => {
                    baiduMap.centerAndZoom(center, zoom);
                }),
        );
    };

    public setViewport = (bounds: BMap.Bounds): void => {
        this.subscription.add(
            this.baiduMap$
                .pipe(filter(isDefinedBMap), take(1))
                .subscribe((baiduMap) => {
                    baiduMap.setViewport(
                        [bounds.getNorthEast(), bounds.getSouthWest()],
                        {
                            margins: [10, 10, 10, 10],
                            enableAnimation: true,
                        },
                    );
                }),
        );
    };

    public setViewportPoint = (points: BMap.Point[]): void => {
        this.subscription.add(
            this.baiduMap$
                .pipe(filter(isDefinedBMap), take(1))
                .subscribe((baiduMap) => {
                    baiduMap.setViewport(points, {
                        margins: [10, 10, 10, 10],
                        enableAnimation: true,
                    });
                }),
        );
    };

    public resetService(): void {
        this.baiduMap$.next(undefined);
        this.baiduMarkerClusterer$.next(undefined);
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }
}
